import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { ModalDialog } from 'components/ModalDialog';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import type { TActNavInfoState } from 'hooks/useScrollToWithNavigationInfo';
import { Grid, GridItem, IconAlert, Informer, Button } from 'uikit';

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useActKC2TemplateUpload } from './hooks/useActKC2TemplateUpload';

import Style from './ImportActKC2TemplateModal.module.css';

interface IExportActKC2TemplateModal {
    onClose: (isUploadSuccess?: boolean) => void;
    workOrderId: number;
}

const title = 'Загрузить АКТ КС-2';
const accept = ['.xlsx'];

export const ImportActKC2TemplateModal: React.FC<IExportActKC2TemplateModal> = (props) => {
    const navigate = useNavigate();
    const { onClose, workOrderId } = props;

    const [files, setFiles] = useState<File[] | undefined>([]);
    const base64Files = useReadFilesAsBase64(files);

    const {
        importActKC2TemplateAsExcelData,
        importActKC2TemplateAsExcelStatus,
        importActKC2TemplateAsExcelError,
        makeRequest,
        onCloseModalError,
        onCreateReport,
        isSubmitted,
        resetData,
    } = useActKC2TemplateUpload(workOrderId, setFiles);

    const onConfirm = useCallback(() => {
        if (base64Files?.length) {
            makeRequest(base64Files[0]);
        }
    }, [base64Files, makeRequest]);

    const onFilesChange = useCallback((newFiles?: File[]) => {
        setFiles(newFiles);
    }, []);

    const onSuccess = useCallback(() => {
        const state = importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.data?.reduce(
            (acc: Pick<TActNavInfoState['actNavInfo'], 'actId' | 'constructionTypeId' | 'allActIds'>, item) => {
                if (item?.actKC2Id) {
                    if (acc.actId < item?.actKC2Id) {
                        acc.actId = item.actKC2Id;
                        acc.constructionTypeId = item.constructionTypeId;
                    }

                    acc.allActIds?.push(item.actKC2Id);
                }

                return acc;
            },
            {
                actId: 0,
                constructionTypeId: 0,
                allActIds: [],
            },
        );

        navigate('./', {
            state: {
                actNavInfo: state,
            },
        });
        resetData();
        onClose(true);
    }, [importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.data, navigate, onClose, resetData]);

    const onLocalClose = useCallback(() => {
        resetData();
        onClose(false);
    }, [onClose, resetData]);

    const isSuccess = useMemo(
        () =>
            importActKC2TemplateAsExcelStatus === RequestStatus.SUCCESS &&
            importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.result === true &&
            !importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.message &&
            !importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.validation?.length,
        [importActKC2TemplateAsExcelData, importActKC2TemplateAsExcelStatus],
    );

    const isSuccessWithErrors = useMemo(
        () =>
            importActKC2TemplateAsExcelStatus === RequestStatus.SUCCESS &&
            importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.result === true &&
            (importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.message ||
                importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel?.validation?.length),
        [importActKC2TemplateAsExcelData, importActKC2TemplateAsExcelStatus],
    );

    return (
        <>
            <ModalDialog
                title={title}
                hasOverlay
                withTitleCloseButton
                onClose={onLocalClose}
                onConfirm={onConfirm}
                btnConfirmDisabled={
                    !base64Files?.length || importActKC2TemplateAsExcelStatus === RequestStatus.FETCHING
                }
                isOpen={!isSuccess}
            >
                <div className={Style.importActKC2TemplateModal}>
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <Informer status="warning" size="s" icon={IconAlert}>
                                Выберите заполненный шаблон для создания Акта КС-2
                            </Informer>
                        </GridItem>
                        <GridItem>
                            <FilesSelector
                                files={files}
                                accept={accept}
                                isLoading={importActKC2TemplateAsExcelStatus === RequestStatus.FETCHING}
                                typeName={accept[0]}
                                manualOperationName="выберите вручную"
                                onFilesChange={onFilesChange}
                                isExternalFilesState
                                locale={{
                                    'file-invalid-type': 'Шаблон КС-2: Неверный формат файла',
                                }}
                            />
                        </GridItem>
                    </Grid>
                </div>
                <ErrorHandlingModal
                    isSuccessProcessed
                    titleText={title}
                    isSubmitted={isSubmitted}
                    statusRequest={importActKC2TemplateAsExcelStatus}
                    dataRequest={importActKC2TemplateAsExcelData?.uerImportActKC2TemplateAsExcel}
                    errorsRequest={importActKC2TemplateAsExcelError}
                    handleCloseModalError={isSuccessWithErrors ? onSuccess : onCloseModalError}
                    handleCreateReport={onCreateReport}
                />
            </ModalDialog>
            {isSuccess && (
                <UploadStatus
                    isOpened
                    handleCloseModal={onClose}
                    statusText="Проверка завершена. Ошибок не обнаружено."
                    titleText={title}
                    status="success"
                >
                    <Button label="Перейти к Акту КС-2" onClick={onSuccess} />
                </UploadStatus>
            )}
        </>
    );
};

ImportActKC2TemplateModal.displayName = 'ImportActKC2TemplateModal';
